import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, prettyPhone } from '../lib/api'

/** Which desk extension belongs to whom, and which numbers are this
 *  organization's own.
 *
 *  NEITHER OF THESE CHANGES ANYTHING ON THE PHONE SYSTEM.
 *
 *  Nothing here creates an extension or edits a dial plan. The operator is
 *  telling the CRM that 320 is Shlomo, and that 15148251194 is one of his
 *  numbers. If somebody moves desks that is an edit on this screen, and the
 *  PBX never hears about it — which is why it belongs in the organization's
 *  own settings rather than the setup console: it changes with the roster, and
 *  the people who know it are the coordinators.
 */
export function Extensions() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.extensions>> | null>(null)
  const [numbers, setNumbers] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const d = await api.extensions()
      setData(d)
      setNumbers(d.inbound_numbers.join('\n'))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load this.')
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function assign(userId: string, extension: string) {
    setError(null)
    setNote(null)
    try {
      await api.extensionAssign(userId, extension.trim() || null)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.')
    }
  }

  async function saveNumbers() {
    setBusy(true)
    setError(null)
    try {
      const list = numbers.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      const saved = await api.inboundNumbers(list)
      setNumbers(saved.numbers.join('\n'))
      setNote(
        saved.numbers.length
          ? `Saved. Calls to ${saved.numbers.length === 1 ? 'this number' : 'these numbers'} ` +
            'are now recorded even when they never reach a phone.'
          : 'Cleared.',
      )
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the numbers.')
    } finally {
      setBusy(false)
    }
  }

  if (!data) return null

  return (
    <>
      <div className="card">
        <h2>Who answers on which extension</h2>
        <p className="blurb">
          So the call log can say <em>who</em> took a call rather than which
          number did. This is a mapping only — nothing here changes anything on
          the phone system, and moving somebody to a different desk is an edit
          here, not a dial-plan change.
        </p>

        {error && <div className="notice">{error}</div>}
        {note && <div className="notice ok">{note}</div>}

        {data.users.filter((u) => !u.disabled).map((u) => (
          <div className="listrow" key={u.id}>
            <span className="nm">{u.display_name}</span>
            <span className="dim">{u.login}</span>
            {u.role_name && <span className="tag">{u.role_name}</span>}
            <span className="spacer" style={{ marginLeft: 'auto' }} />
            <input
              className="extbox"
              defaultValue={u.pbx_extension ?? ''}
              placeholder="320"
              aria-label={`Extension for ${u.display_name}`}
              onBlur={(e) => {
                if ((e.target.value.trim() || null) !== (u.pbx_extension ?? null)) {
                  assign(u.id, e.target.value)
                }
              }}
            />
          </div>
        ))}

        {/* The operator should not have to remember his own numbering plan.
            These calls already went past, so the system offers what it saw. */}
        {data.unclaimed_extensions.length > 0 && (
          <p className="footnote">
            Extensions the phone system has rung that nobody here has claimed:{' '}
            {data.unclaimed_extensions
              .map((e) => `${e.extension} (${e.calls})`)
              .join(', ')}.
          </p>
        )}
      </div>

      <div className="card">
        <h2>This organization's own numbers</h2>
        <p className="blurb">
          The numbers people ring to reach you. One per line. Without them, a
          call that dies in a greeting, an answering message, or while the
          caller waits is invisible — the system can only claim a call once one
          of your phones is actually rung, and those calls never get that far.
          They are exactly the calls nobody had the chance to answer.
        </p>

        {!data.configured && (
          <div className="notice">
            No phone connection is configured for this organization yet, so
            there is nothing for these to attach to.
          </div>
        )}

        <div className="field">
          <label>Inbound numbers</label>
          <textarea
            rows={4}
            value={numbers}
            placeholder={'15148251194'}
            onChange={(e) => setNumbers(e.target.value)}
          />
        </div>
        <button
          className="primary compact"
          disabled={busy || !data.configured}
          onClick={saveNumbers}
        >
          {busy ? 'Saving…' : 'Save numbers'}
        </button>

        {data.inbound_numbers.length > 0 && (
          <p className="footnote">
            Currently watching{' '}
            {data.inbound_numbers.map((n) => prettyPhone(n)).join(', ')}.
            Type them however you like — they are matched on the digits, so
            (514) 825-1194 and 15148251194 are the same number.
          </p>
        )}
      </div>
    </>
  )
}
